"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { useContactModal } from '../../../../contexts/ContactModalContext';

const services = [
    'User Research', 'UX Strategy', 'UI Design', 'Wireframing',
    'Usability Testing', 'Conversion UX', 'Design Systems', 'Prototyping',
];

/* ── Floating UI card illustration ── */
const FloatingCards = () => (
    <div className="relative w-full h-full" style={{ minHeight: 420 }}>
        <div style={{ position: 'absolute', top: '25%', left: '35%', width: 260, height: 260, background: 'radial-gradient(circle, rgba(206,255,26,0.07) 0%, transparent 65%)', borderRadius: '50%', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', top: '55%', left: '10%', width: 180, height: 180, background: 'radial-gradient(circle, rgba(55,48,163,0.15) 0%, transparent 65%)', borderRadius: '50%', pointerEvents: 'none' }} />

        {/* Main phone mockup */}
        <motion.div
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ position: 'absolute', top: '2%', left: '12%', width: 145, height: 260, borderRadius: 22, background: '#111', border: '1.5px solid rgba(255,255,255,0.1)', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.6)' }}
        >
            <div style={{ position: 'absolute', top: 8, left: '50%', transform: 'translateX(-50%)', width: 36, height: 6, background: '#0a0a0a', borderRadius: 4, zIndex: 2 }} />
            <div style={{ padding: '22px 12px 12px', display: 'flex', flexDirection: 'column', gap: 7, height: '100%', background: '#0d1117' }}>
                <div style={{ background: '#1a1f2e', borderRadius: 7, padding: 9, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ background: '#ceff1a', borderRadius: 3, height: 5, width: '60%' }} />
                    <div style={{ background: 'rgba(255,255,255,0.14)', borderRadius: 3, height: 4, width: '80%' }} />
                    <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 3, height: 4, width: '55%' }} />
                </div>
                <div style={{ display: 'flex', gap: 5 }}>
                    <div style={{ flex: 1, background: '#1a1f2e', borderRadius: 5, height: 50 }} />
                    <div style={{ flex: 1, background: '#1a1f2e', borderRadius: 5, height: 50 }} />
                </div>
                <div style={{ background: '#ceff1a', borderRadius: 5, height: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ color: '#080400', fontSize: 8, fontWeight: 800, fontFamily: 'sans-serif' }}>Get Started →</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 2 }}>
                    {[80, 65, 72, 58].map((w, i) => (
                        <div key={i} style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 2, height: 4, width: `${w}%` }} />
                    ))}
                </div>
                <div style={{ flex: 1, background: 'rgba(55,48,163,0.2)', borderRadius: 5, border: '1px solid rgba(55,48,163,0.3)' }} />
            </div>
        </motion.div>

        {/* Desktop card */}
        <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
            style={{ position: 'absolute', top: '20%', left: '44%', width: 178, height: 118, borderRadius: 10, background: '#161b27', border: '1px solid rgba(255,255,255,0.08)', padding: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)' }}
        >
            <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                {['#ff5f57','#ffbd2e','#28c840'].map((c,i) => (
                    <div key={i} style={{ width: 6, height: 6, borderRadius: '50%', background: c }} />
                ))}
            </div>
            <div style={{ display: 'flex', gap: 7 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: 1 }}>
                    <div style={{ background: '#ceff1a', borderRadius: 2, height: 6, width: '70%' }} />
                    <div style={{ background: 'rgba(255,255,255,0.14)', borderRadius: 2, height: 4, width: '90%' }} />
                    <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 2, height: 4, width: '75%' }} />
                    <div style={{ display: 'flex', gap: 4, marginTop: 3 }}>
                        <div style={{ background: '#ceff1a', borderRadius: 3, height: 13, width: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <span style={{ color: '#080400', fontSize: 5.5, fontWeight: 800, fontFamily: 'sans-serif' }}>CTA</span>
                        </div>
                        <div style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 3, height: 13, width: 32 }} />
                    </div>
                </div>
                <div style={{ width: 44, height: 64, background: 'rgba(55,48,163,0.35)', borderRadius: 5, border: '1px solid rgba(55,48,163,0.4)' }} />
            </div>
        </motion.div>

        {/* Badge: User Satisfaction */}
        <motion.div
            animate={{ y: [0, -8, 0], rotate: [-1, 1, -1] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            style={{ position: 'absolute', top: '4%', left: '60%', background: '#ceff1a', borderRadius: 10, padding: '8px 12px', boxShadow: '0 8px 28px rgba(206,255,26,0.22)' }}
        >
            <div style={{ fontSize: 16, fontWeight: 900, color: '#080400', fontFamily: 'sans-serif', lineHeight: 1 }}>↑84%</div>
            <div style={{ fontSize: 7, fontWeight: 700, color: 'rgba(8,4,0,0.55)', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>User Satisfaction</div>
        </motion.div>

        {/* Badge: Conversion */}
        <motion.div
            animate={{ y: [0, 9, 0] }}
            transition={{ duration: 5.5, repeat: Infinity, ease: 'easeInOut', delay: 1.5 }}
            style={{ position: 'absolute', top: '58%', left: '52%', background: '#3730a3', borderRadius: 10, padding: '8px 12px', border: '1px solid rgba(255,255,255,0.1)', boxShadow: '0 8px 28px rgba(55,48,163,0.3)' }}
        >
            <div style={{ fontSize: 16, fontWeight: 900, color: '#ffffff', fontFamily: 'sans-serif', lineHeight: 1 }}>+62%</div>
            <div style={{ fontSize: 7, fontWeight: 700, color: 'rgba(255,255,255,0.5)', fontFamily: 'sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginTop: 2 }}>Conversion Rate</div>
        </motion.div>

        {/* Figma cursor */}
        <motion.div
            animate={{ x: [0, 18, 36, 18, 0], y: [0, 8, 0, -8, 0] }}
            transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
            style={{ position: 'absolute', top: '43%', left: '28%', zIndex: 10 }}
        >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 3L3 16L6.8 12.5L9.5 18.5L11.5 17.8L8.8 11.8L14 11.8Z" fill="white" stroke="#0a0a0a" strokeWidth="1" strokeLinejoin="round"/>
            </svg>
            <div style={{ position: 'absolute', top: 18, left: 14, background: '#5c6af5', borderRadius: 4, padding: '2px 6px', whiteSpace: 'nowrap' }}>
                <span style={{ color: 'white', fontSize: 7, fontWeight: 700, fontFamily: 'sans-serif' }}>lekhana gowda</span>
            </div>
        </motion.div>

        {/* Colour palette chip */}
        <motion.div
            animate={{ y: [0, -6, 0] }}
            transition={{ duration: 4.5, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
            style={{ position: 'absolute', bottom: '6%', left: '14%', background: '#1a1f2e', borderRadius: 9, padding: '7px 11px', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', gap: 5, alignItems: 'center' }}
        >
            <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 7, fontFamily: 'sans-serif', letterSpacing: '0.1em', textTransform: 'uppercase', marginRight: 3 }}>Palette</span>
            {['#ceff1a','#3730a3','#0a0a0a','#ffffff','#ff6b6b'].map((c,i) => (
                <div key={i} style={{ width: 13, height: 13, borderRadius: '50%', background: c, border: '1px solid rgba(255,255,255,0.1)' }} />
            ))}
        </motion.div>
    </div>
);

export default function UXHero() {
    const { openModal } = useContactModal();

    return (
        <section className="relative overflow-hidden" style={{ background: '#0a0a0a' }}>

            {/* Grid overlay */}
            <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'linear-gradient(rgba(206,255,26,1) 1px, transparent 1px), linear-gradient(90deg, rgba(206,255,26,1) 1px, transparent 1px)',
                backgroundSize: '72px 72px',
                opacity: 0.025,
            }} />

            {/* Large faded "UX" bg text */}
            <div className="absolute inset-0 flex items-start justify-end pr-8 pt-16 pointer-events-none select-none overflow-hidden">
                <span style={{
                    fontSize: 'clamp(160px, 22vw, 340px)',
                    fontWeight: 900,
                    color: 'transparent',
                    WebkitTextStroke: '1px rgba(255,255,255,0.035)',
                    fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                    letterSpacing: '-0.06em',
                    lineHeight: 1,
                    userSelect: 'none',
                }}>UX</span>
            </div>

            <div className="relative z-10 max-w-[1360px] mx-auto px-6 md:px-16 lg:px-24 pt-36 pb-0">

                {/* ── TOP: Tag + Full-width Headline ── */}
                <div className="flex flex-col items-start gap-6 mb-14">

                    {/* Tag */}
                    <motion.div
                        initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}
                        className="flex items-center gap-3"
                    >
                        <div style={{ width: 28, height: 2, background: '#ceff1a', borderRadius: 2 }} />
                        <span style={{ color: '#ceff1a', fontSize: 11, fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}>
                            UI/UX Design Agency
                        </span>
                    </motion.div>

                    {/* Headline — 2 lines, full width */}
                    <motion.h1
                        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }}
                        className="font-black leading-[1.05] tracking-wide w-full"
                        style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', color: '#ffffff', fontSize: 'clamp(32px, 3.6vw, 56px)' }}
                    >
                        Great Products Aren&apos;t Built Around Features.<br />
                        They&apos;re Built Around{' '}
                        <span style={{ color: '#ceff1a' }}>People.</span>
                    </motion.h1>
                </div>

                {/* ── BOTTOM: Two-column — body copy left, illustration right ── */}
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-12 lg:gap-16 items-start">

                    {/* LEFT: Body copy + CTAs */}
                    <div className="flex flex-col gap-8">

                        {/* Three body paragraphs */}
                        <div className="flex flex-col gap-5">
                            <motion.p
                                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.22 }}
                                style={{ color: 'rgba(251,253,253,0.55)', fontSize: 16, lineHeight: 1.85, fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                            >
                                Most digital products don&apos;t fail because they&apos;re missing functionality. They fail because users feel{' '}
                                <span style={{ color: 'rgba(251,253,253,0.9)', fontWeight: 600 }}>confused, frustrated, or overwhelmed.</span>
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
                                style={{ color: 'rgba(251,253,253,0.55)', fontSize: 16, lineHeight: 1.85, fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                            >
                                Every unnecessary click, unclear interface, and broken user journey creates friction.{' '}
                                <span style={{ color: '#ceff1a', fontWeight: 600 }}>And friction costs businesses customers.</span>
                            </motion.p>

                            <motion.p
                                initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.38 }}
                                style={{ color: 'rgba(251,253,253,0.55)', fontSize: 16, lineHeight: 1.85, fontFamily: 'var(--font-manrope), Manrope, sans-serif' }}
                            >
                                At Lead Workz, we design digital experiences around how people think, behave, and make decisions — combining user research, behavioural psychology, and interface design to create products that feel{' '}
                                <span style={{ color: 'rgba(251,253,253,0.9)', fontWeight: 600 }}>intuitive, reduce friction, and improve engagement.</span>
                            </motion.p>
                        </div>

                        {/* Highlight quote */}
                        <motion.div
                            initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.45 }}
                            style={{ borderLeft: '3px solid #ceff1a', paddingLeft: 20, marginTop: 4 }}
                        >
                            <p style={{ color: 'rgba(206,255,26,0.85)', fontSize: 15, fontWeight: 600, lineHeight: 1.7, fontFamily: 'var(--font-manrope), Manrope, sans-serif', fontStyle: 'italic' }}>
                                &ldquo;Great UX isn&apos;t about making products look better. It&apos;s about making them easier to use.&rdquo;
                            </p>
                        </motion.div>

                        {/* CTAs */}
                        <motion.div
                            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.52 }}
                            className="flex flex-wrap gap-4 items-center pt-2"
                        >
                            <button
                                onClick={openModal}
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_rgba(206,255,26,0.3)]"
                                style={{ background: '#ceff1a', color: '#080400', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.06em' }}
                            >
                                Book a UX Strategy Session
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8h10M9 4l4 4-4 4" stroke="#080400" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </button>
                            <Link
                                href="/our-work/web-design"
                                className="inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-all duration-300 hover:border-white/30"
                                style={{ border: '1.5px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.65)', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.04em' }}
                            >
                                View Our Work
                            </Link>
                        </motion.div>
                    </div>

                    {/* RIGHT: Floating illustration */}
                    <motion.div
                        initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.8, delay: 0.3 }}
                        className="hidden lg:block relative"
                        style={{ height: 440 }}
                    >
                        <FloatingCards />
                    </motion.div>
                </div>

                {/* ── Scrolling ticker ── */}
                <div className="relative mt-16 overflow-hidden" style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: 18, paddingBottom: 18 }}>
                    <motion.div
                        animate={{ x: ['0%', '-50%'] }}
                        transition={{ duration: 18, repeat: Infinity, ease: 'linear' }}
                        className="flex gap-10 whitespace-nowrap"
                        style={{ width: 'max-content' }}
                    >
                        {[...services, ...services].map((s, i) => (
                            <span key={i} className="flex items-center gap-10">
                                <span style={{ color: 'rgba(255,255,255,0.28)', fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-clash), "Clash Display", sans-serif', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                                    {s}
                                </span>
                                <span style={{ color: '#ceff1a', fontSize: 9 }}>✦</span>
                            </span>
                        ))}
                    </motion.div>
                </div>

            </div>
        </section>
    );
}
